import React, { useState } from "react";
import { MessageSquare, Send, Trash2, SmilePlus, CornerDownRight } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "./ui/popover";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useAuth } from "../lib/auth";
import { ReportButton } from "./ReportButton";

export const EMOJIS = ["👍", "🔥", "😂", "🎾", "💪", "❤️", "👏", "😮"];

export const timeAgo = (iso) => {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "ora";
  if (diff < 3600) return `${Math.floor(diff / 60)} min fa`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h fa`;
  return `${Math.floor(diff / 86400)} g fa`;
};

export const renderMentions = (text) =>
  text.split(/(@[\wÀ-ÿ'-]+)/g).map((part, i) =>
    part.startsWith("@")
      ? <span key={i} className="text-[#ccff00] font-semibold">{part}</span>
      : part
  );

export const MentionInput = ({ value, onChange, onSubmit, placeholder, names, testid, h = "h-10" }) => {
  const [open, setOpen] = useState(false);
  const m = value.match(/@([^\s@]*)$/);
  const query = m ? m[1].toLowerCase() : null;
  const suggestions = query !== null
    ? names.filter((n) => n.toLowerCase().includes(query)).slice(0, 5)
    : [];
  const pick = (name) => {
    onChange(value.replace(/@([^\s@]*)$/, `@${name} `));
    setOpen(false);
  };
  return (
    <div className="relative flex-1">
      {open && suggestions.length > 0 && (
        <div className="absolute bottom-full mb-1 left-0 right-0 bg-[#121b2d] border border-[#1e2c4a] rounded-xl overflow-hidden z-50 shadow-xl" data-testid="mention-suggestions">
          {suggestions.map((n) => (
            <button
              key={n}
              onMouseDown={(e) => { e.preventDefault(); pick(n); }}
              data-testid={`mention-suggestion-${n}`}
              className="w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-slate-800"
            >
              <span className="text-[#ccff00]">@</span>{n}
            </button>
          ))}
        </div>
      )}
      <Input
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onKeyDown={(e) => { if (e.key === "Enter") { setOpen(false); onSubmit(); } }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        data-testid={testid}
        className={`bg-slate-900/60 border-slate-700 text-slate-100 ${h} ${h === "h-9" ? "text-sm" : ""}`}
      />
    </div>
  );
};

export const CommentsDialog = ({ match, playerName, names = [], onClose, onAdd, onDelete, onReact, onReply, onDeleteReply, onRequireName }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  if (!match) return null;
  const comments = match.comments || [];

  const submit = async () => {
    const t = text.trim();
    if (!t) return;
    if (!playerName) { onRequireName(); return; }
    setBusy(true);
    try {
      await onAdd(match.id, playerName, t);
      setText("");
    } finally {
      setBusy(false);
    }
  };

  const react = (commentId, emoji) => {
    if (!playerName) { onRequireName(); return; }
    onReact(match.id, commentId, emoji);
  };

  return (
    <Dialog open={!!match} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#121b2d] border-[#1e2c4a] text-slate-100 max-w-md max-h-[85vh] flex flex-col" data-testid="comments-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-[#ccff00]" /> Commenti
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {match.venue} · {match.date} {match.time}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-2 py-1 min-h-[80px]">
          {comments.length === 0 ? (
            <p className="text-slate-500 text-sm text-center py-6">Nessun commento. Scrivi il primo!</p>
          ) : (
            comments.map((c) => (
              <CommentItem
                key={c.id}
                comment={c}
                matchId={match.id}
                playerName={playerName}
                names={names}
                onDelete={onDelete}
                onReact={react}
                onReply={onReply}
                onDeleteReply={onDeleteReply}
                onRequireName={onRequireName}
              />
            ))
          )}
        </div>

        <div className="flex gap-2 pt-2 border-t border-[#1e2c4a]">
          <MentionInput
            value={text}
            onChange={setText}
            onSubmit={submit}
            names={names}
            placeholder={playerName ? "Scrivi un commento... usa @ per taggare" : "Imposta prima il tuo nome"}
            testid="comment-input"
          />
          <Button onClick={submit} disabled={busy || !text.trim()} data-testid="send-comment-btn" className="bg-[#ccff00] hover:bg-[#aeea00] text-[#090d16] font-bold rounded-xl px-4 shrink-0">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const CommentItem = ({ comment: c, matchId, playerName, names = [], onDelete, onReact, onReply, onDeleteReply, onRequireName }) => {
  const { user } = useAuth();
  const isMine = (author) => !!playerName && author.toLowerCase() === playerName.toLowerCase();
  const canDelete = (author) => user?.role === "admin" || isMine(author);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [busy, setBusy] = useState(false);
  const reactions = c.reactions || {};
  const replies = c.replies || [];

  const sendReply = async () => {
    const t = replyText.trim();
    if (!t) return;
    if (!playerName) { onRequireName(); return; }
    setBusy(true);
    try {
      await onReply(matchId, c.id, playerName, t);
      setReplyText("");
      setReplyOpen(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`comment-${c.id}`} className="rounded-xl bg-slate-900/60 border border-slate-700/60 px-3 py-2.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-bold text-[#ccff00]">{c.name}</span>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-slate-500">{timeAgo(c.created_at)}</span>
          {!isMine(c.name) && (
            <ReportButton testid={`report-comment-${c.id}`} payload={{ kind: "comment", target_id: c.id, match_id: matchId, author: c.name, text: c.text }} />
          )}
          {canDelete(c.name) && (
            <button onClick={() => onDelete(matchId, c.id)} data-testid={`delete-comment-${c.id}`} className="text-slate-500 hover:text-[#ff4d4d]">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
      <p className="text-sm text-slate-200 mt-0.5 break-words">{renderMentions(c.text)}</p>

      <div className="flex flex-wrap items-center gap-1.5 mt-2">
        {Object.entries(reactions).map(([emoji, users]) => {
          const mine = playerName && users.some((u) => u.toLowerCase() === playerName.toLowerCase());
          return (
            <button
              key={emoji}
              onClick={() => onReact(c.id, emoji)}
              data-testid={`reaction-${c.id}-${emoji}`}
              className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-xs border transition-colors ${
                mine ? "bg-[#ccff00]/15 border-[#ccff00]/50 text-[#ccff00]" : "bg-slate-800/60 border-slate-700 text-slate-300 hover:border-slate-500"
              }`}
              title={users.join(", ")}
            >
              <span>{emoji}</span><span className="font-bold">{users.length}</span>
            </button>
          );
        })}
        <Popover>
          <PopoverTrigger asChild>
            <button data-testid={`add-reaction-${c.id}`} className="w-6 h-6 rounded-full bg-slate-800/60 border border-slate-700 flex items-center justify-center text-slate-400 hover:text-[#ccff00] hover:border-[#ccff00]/40">
              <SmilePlus className="w-3.5 h-3.5" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-2 bg-[#121b2d] border-[#1e2c4a]" align="start">
            <div className="flex gap-1">
              {EMOJIS.map((e) => (
                <button
                  key={e}
                  onClick={() => onReact(c.id, e)}
                  data-testid={`pick-reaction-${c.id}-${e}`}
                  className="w-8 h-8 rounded-lg hover:bg-slate-800 text-lg flex items-center justify-center"
                >
                  {e}
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
        <button
          onClick={() => (playerName ? setReplyOpen((v) => !v) : onRequireName())}
          data-testid={`reply-toggle-${c.id}`}
          className="flex items-center gap-1 text-xs text-slate-400 hover:text-[#ccff00] ml-1"
        >
          <CornerDownRight className="w-3.5 h-3.5" /> Rispondi
        </button>
      </div>

      {replies.length > 0 && (
        <div className="mt-2 pl-3 border-l-2 border-[#1e2c4a] space-y-1.5">
          {replies.map((r) => (
            <div key={r.id} data-testid={`reply-${r.id}`} className="py-0.5">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-bold text-[#00e5ff]">{r.name}</span>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-slate-500">{timeAgo(r.created_at)}</span>
                  {!isMine(r.name) && (
                    <ReportButton testid={`report-reply-${r.id}`} payload={{ kind: "reply", target_id: r.id, match_id: matchId, comment_id: c.id, author: r.name, text: r.text }} />
                  )}
                  {canDelete(r.name) && (
                    <button onClick={() => onDeleteReply(matchId, c.id, r.id)} data-testid={`delete-reply-${r.id}`} className="text-slate-500 hover:text-[#ff4d4d]">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
              <p className="text-sm text-slate-300 break-words">{renderMentions(r.text)}</p>
            </div>
          ))}
        </div>
      )}

      {replyOpen && (
        <div className="flex gap-2 mt-2 pl-3">
          <MentionInput
            value={replyText}
            onChange={setReplyText}
            onSubmit={sendReply}
            names={names}
            placeholder="Rispondi... usa @ per taggare"
            testid={`reply-input-${c.id}`}
            h="h-9"
          />
          <Button onClick={sendReply} disabled={busy || !replyText.trim()} data-testid={`send-reply-btn-${c.id}`} className="bg-[#ccff00] hover:bg-[#aeea00] text-[#090d16] font-bold rounded-xl px-3 h-9 shrink-0">
            <Send className="w-3.5 h-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
};
